import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Dialog } from '@/components/ui/Dialog';

/**
 * A button with an info icon that opens a dialog explaining how the
 * net worth projection (return rate and contributions) is calculated.
 */
export function ProjectionInfoButton() {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="p-0.5 text-gray-400 hover:text-accent transition-colors"
                title="About the projection"
            >
                <InformationCircleIcon className="h-5 w-5" />
            </button>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                title="About the projection"
                size="sm"
            >
                <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <p>
                        Assets and debts are projected separately, starting from your latest
                        values:{' '}
                        <strong className="text-gray-900 dark:text-white">
                            assets × (1 + return rate) + yearly contributions
                        </strong>
                        , while debts follow your historical average yearly change — negative
                        means paydown. Net worth = assets − debts; neither side goes below
                        zero. The default horizon is 20 years.
                    </p>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Return rate:</p>
                        <ul className="list-disc list-inside space-y-0.5 text-gray-500 dark:text-gray-400">
                            <li>
                                Only assets where you record cash flows (deposits/withdrawals) are
                                measured. Tracking starts at the first recorded cash flow, using
                                the closest valuation as the starting point.
                            </li>
                            <li>
                                Assets without cash flows still count toward your net worth, but
                                their value changes are not treated as investment returns — they
                                may include untracked deposits. Archived assets stay part of the
                                historical figures.
                            </li>
                            <li>
                                Each year, all tracked assets are combined and weighted by value:
                                (end value − net deposits − start value) / start value. Larger
                                assets influence the rate more than smaller ones.
                            </li>
                            <li>
                                The rate is the annualized geometric average: complete years count
                                in full, the partial first tracking year counts only for the time
                                actually measured, and the current year is excluded until it ends.
                            </li>
                            <li>
                                Years where a value is only carried forward (no new valuation)
                                measure as 0% and pull the average toward zero. A loss worse than
                                −99% in a single year is capped at −99% rather than ignored.
                            </li>
                            <li>
                                Values are converted to your display currency, so exchange rate
                                movements are part of the measured return.
                            </li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Yearly contributions:</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            Your recorded net cash flows (deposits − withdrawals) averaged over
                            every complete year since your first recorded cash flow — years
                            without flows count too, so occasional lump sums are not
                            overestimated. A negative value models retirement drawdown.
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Withdrawals & depletion:</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            A negative net yearly cash flow models retirement spending — it is
                            withdrawn from assets each year. Assets never drop below zero; if they
                            reach it, a "funds depleted" note shows the first year that happens.
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Financial independence (opt-in):</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            FI is an optional view, off by default. With it on and a subset of collections
                            selected, the projection runs one compositional simulation: your net worth line
                            is the sum of three buckets — your investable assets (the selected collections,
                            growing at the investable rate and receiving all of your contributions), your
                            unchecked collections (frozen at today's value — no growth, no loss), and your
                            outside debts (following your debt trend). The second line is the investable
                            bucket, and the FI target and year are measured against it. Both rates are
                            shown: the editable investable rate lives in the Financial independence panel and
                            drives that line's growth, while the header keeps your whole-portfolio blended
                            rate for reference. With FI on, the "funds depleted" note is evaluated on the
                            investable bucket — the meaningful signal when contributions are negative.
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">FI target (4% rule):</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            Your financial independence target is your annual expenses × a multiplier
                            derived from the withdrawal rate (100 ÷ rate). The default 4% withdrawal rate
                            gives the classic 25× target. When FI mode is on, a dashed line marks that level,
                            and an "FI reached" note shows the first projected year your investable assets
                            cross it.
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Simulate withdrawals after FI (real terms):</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            An optional switch in the Financial independence panel. When on, the whole
                            projection runs in today's money — an inflation assumption (editable, default
                            from your settings) is subtracted from every growth rate, so each value and your
                            FI target stay interpretable as today's money. Because growth is real, FI may land
                            a little later than the nominal view — that is the honest math. From the year after
                            you reach FI, contributions stop and your annual expenses are withdrawn every year
                            (the classic 4%-rule drawdown); collections you leave unchecked stay frozen, which
                            in real terms means they keep pace with inflation. The panel shows the year
                            withdrawals begin, and if the pot ever runs dry a "pot runs out" warning marks that
                            year.
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Fallbacks:</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            If you have never recorded cash flows, the rate is based on the
                            growth of your total assets instead. With less than one full year of
                            measurable performance, a conservative 5% default is applied.
                        </p>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                        Tap any highlighted value in the sentence to override it. Overrides are
                        remembered on this device; the reset arrow restores the calculated
                        values.
                    </p>
                </div>
            </Dialog>
        </>
    );
}
